import React, { useState, useEffect, useRef, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { UserPlus } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEmployees } from '../../../hooks/useEmployees';
import EmployeeTable from './EmployeeTable';
import EmployeeModal from './EmployeeModal';
import EmployeeSlideout from './EmployeeSlideout';
import StatusChangeModal from './StatusChangeModal';

const EmployeeManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [statusChangeEmployee, setStatusChangeEmployee] = useState(null);
  const [slideoutOpen, setSlideoutOpen] = useState(false);
  const [slideoutEmployee, setSlideoutEmployee] = useState(null);
  const hasOpenedFromUrl = useRef(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const cancelCloseRef = useRef(null);

  const {
    employees,
    statistics,
    loading,
    error,
    setError,
    employeeOperations,
    pagination,
    filters,
    setFilters,
    changePage,
    refreshData
  } = useEmployees();

  // Handle URL query parameter for opening modal - only once
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const openModalParam = searchParams.get('openModal');
    
    if (openModalParam === 'employee' && !hasOpenedFromUrl.current && !modalOpen) {
      hasOpenedFromUrl.current = true;
      setModalType('employee');
      setEditingEmployee(null);
      setModalOpen(true);
      // Clear the openModal param from URL immediately
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('openModal');
      navigate(newUrl.pathname + newUrl.search, { replace: true });
    }
  }, [location.search, modalOpen, navigate]);

  // Modal handlers (for Add Employee only)
  const openModal = (type, employee = null) => {
    setModalType(type);
    setEditingEmployee(employee);
    setModalOpen(true);
  };

  const closeModal = (force = false) => {
    // If there are unsaved changes and not forcing, show confirmation
    if (hasUnsavedChanges && !force && !showConfirmClose) {
      setShowConfirmClose(true);
      return;
    }
    setModalOpen(false);
    setModalType(null);
    setEditingEmployee(null);
    setHasUnsavedChanges(false);
    setShowConfirmClose(false);
    // Reset the URL flag when closing so it can open again if needed
    hasOpenedFromUrl.current = false;
  };

  const handleDialogClose = () => {
    // Called when clicking outside or pressing Escape
    if (showConfirmClose) return; // Don't close if confirmation is showing
    closeModal();
  };

  const handleConfirmDiscard = () => {
    setShowConfirmClose(false);
    closeModal(true); // Force close
  };

  const handleCancelClose = () => {
    setShowConfirmClose(false);
  };

  // Slideout handlers (for Edit Employee)
  const openSlideout = (employee) => {
    setSlideoutEmployee(employee);
    setSlideoutOpen(true);
  };

  const closeSlideout = () => {
    setSlideoutOpen(false);
    // Delay clearing employee data until after close animation completes
    // This prevents the slideout from showing empty content during the transition
    setTimeout(() => {
      setSlideoutEmployee(null);
    }, 500);
  };

  const handleEmployeeUpdated = async () => {
    // Close modal FIRST, then refresh in background
    // This prevents loading state from interfering with Headless UI transition
    closeSlideout();

    // Delay to let modal complete closing animation (Headless UI uses 400ms ease-in)
    // We wait 500ms to ensure the animation fully completes before refreshing
    setTimeout(async () => {
      await refreshData();
    }, 500);
  };

  // Status change handlers
  const openStatusChange = (employee) => {
    setStatusChangeEmployee(employee);
  };

  const closeStatusChange = () => {
    setStatusChangeEmployee(null);
  };

  // Handle employee form submission
  const handleEmployeeSubmit = async (employeeData) => {
    try {
      if (editingEmployee) {
        await employeeOperations.update(editingEmployee.id, employeeData);
      } else {
        await employeeOperations.create(employeeData);
      }
      // Force close without unsaved changes check - data was just saved successfully
      closeModal(true);
    } catch (error) {
      console.error('Employee operation failed:', error);
      // Error will be displayed by the hook
    }
  };

  // Handle status change submission
  const handleStatusChange = async (employeeId, newStatus, reason = null) => {
    try {
      await employeeOperations.changeStatus(employeeId, newStatus, reason);
      closeStatusChange();
    } catch (error) {
      console.error('Status change failed:', error);
      // Error will be displayed by the hook
    }
  };

  // Handle employee deletion (status change to terminated)
  const handleEmployeeDelete = (employee) => {
    openStatusChange(employee);
  };

  const getModalTitle = () => {
    if (modalType === 'employee') {
      return editingEmployee ? 'Edit Employee' : 'Add Employee';
    }
    return '';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Error Display */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Employee Table */}
      <EmployeeTable
        employees={employees}
        statistics={statistics}
        pagination={pagination}
        filters={filters}
        onFiltersChange={setFilters}
        onEdit={openSlideout}
        onDelete={handleEmployeeDelete}
        onCreateClick={() => openModal('employee')}
        onPageChange={changePage}
      />

      {/* Employee Modal - Headless UI */}
      <Transition.Root show={modalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleDialogClose} initialFocus={cancelCloseRef}>
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          {/* Modal Container */}
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-2xl transition-all w-full sm:my-8 sm:w-full sm:max-w-2xl max-h-[90vh] flex flex-col">
                  {/* Header - matching other modals */}
                  <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-brand-light rounded-lg">
                        <UserPlus className="w-5 h-5 text-brand" />
                      </div>
                      <div>
                        <Dialog.Title as="h2" className="text-lg font-semibold text-gray-900">
                          {getModalTitle()}
                        </Dialog.Title>
                        <p className="text-sm text-gray-500">
                          {editingEmployee ? 'Update employee information' : 'Add a new team member'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={closeModal}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Scrollable Content */}
                  <div className="flex-1 overflow-y-auto">
                    <div className="p-6">
                      {modalType === 'employee' && (
                        <EmployeeModal
                          initialData={editingEmployee}
                          onSubmit={handleEmployeeSubmit}
                          onCancel={closeModal}
                          onUnsavedChanges={setHasUnsavedChanges}
                          onLoadingChange={setIsFormLoading}
                        />
                      )}
                    </div>
                  </div>

                  {/* Footer - Fixed outside scrollable content */}
                  {modalType === 'employee' && (
                    <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
                      <button
                        type="button"
                        onClick={closeModal}
                        className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        disabled={isFormLoading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        form="employee-form"
                        className="px-4 py-2 text-sm bg-brand text-white rounded-lg font-medium hover:bg-brand-hover transition-colors disabled:opacity-50 flex items-center"
                        disabled={isFormLoading}
                      >
                        {isFormLoading && (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-800 mr-2"></div>
                        )}
                        {editingEmployee ? 'Update Employee' : 'Create Employee'}
                      </button>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Unsaved Changes Confirmation Dialog */}
      <Transition.Root show={showConfirmClose} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" onClose={handleCancelClose} initialFocus={cancelCloseRef}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-[70] overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white px-4 pb-4 pt-5 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                        Unsaved Changes
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          You have unsaved changes. Are you sure you want to close without saving?
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      ref={cancelCloseRef}
                      className="inline-flex w-full justify-center rounded-lg bg-white px-3 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors duration-200 sm:col-start-1"
                      onClick={handleCancelClose}
                    >
                      Keep Editing
                    </button>
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-lg bg-red-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 transition-colors duration-200 sm:col-start-2"
                      onClick={handleConfirmDiscard}
                    >
                      Discard Changes
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Status Change Modal */}
      {statusChangeEmployee && (
        <StatusChangeModal
          employee={statusChangeEmployee}
          onSubmit={handleStatusChange}
          onCancel={closeStatusChange}
        />
      )}

      {/* Employee Slideout (for editing) */}
      <EmployeeSlideout
        isOpen={slideoutOpen}
        onClose={closeSlideout}
        employee={slideoutEmployee}
        onEmployeeUpdated={handleEmployeeUpdated}
      />
    </div>
  );
};

export default EmployeeManagement;